import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import useTranslation from '../../i18n/useTranslation';
import WEB_PATHS, { CORPORATE_WEB_PATHS } from '../../constants/webPaths';
import { FaFacebook, FaLinkedin, FaYoutube } from 'react-icons/fa';
import { Instagram, LineApp, Tiktok } from '@ui-kit/icons';
import { IconType } from 'react-icons';
import Image from 'next/image';
import { useCatalogMenu } from '../../hooks/useCatalogMenu';
import { DEFAULT_LOGO } from '@constants/images';
import { LanguageDropdown } from '@ui-kit/v2/LanguageDropdown/LanguageDropdown';

function SocialMediaButton({ Icon, url }: { Icon: IconType; url: string }) {
  return (
    <Link href={url} passHref>
      <a>
        <Icon className="h-5 w-5" />
      </a>
    </Link>
  );
}

interface IFooterRedirectButton {
  header: string;
  url?: string;
  children: {
    title: string;
    url: string;
  }[];
}
export default function FooterExtended() {
  const { catalogMenu } = useCatalogMenu();
  const { t } = useTranslation();

  const footerSocialMediaIconsConfigs = [
    {
      icon: FaFacebook,
      url: t('extendedFooter.socialMedia.facebook'),
    },
    {
      icon: FaYoutube,
      url: t('extendedFooter.socialMedia.youtube'),
    },
    {
      icon: FaLinkedin,
      url: t('extendedFooter.socialMedia.linkedIn'),
    },
    {
      icon: LineApp,
      url: t('extendedFooter.socialMedia.lineApp'),
    },
  ];

  const defaultFooterRedirectButton: IFooterRedirectButton[] = [
    {
      header: t('extendedFooter.newCourses'),
      children: [
        {
          title: t('extendedFooter.recommendedCourses'),
          url: WEB_PATHS.COURSE_RECOMMENDED_LIST,
        },
        {
          title: t('extendedFooter.popularCourses'),
          url: WEB_PATHS.COURSE_POPULAR_LIST,
        },
        {
          title: t('extendedFooter.allInstructors'),
          url: '/search?type=instructor',
        },
      ],
    },
    {
      header: t('extendedFooter.openCourses'),
      url: WEB_PATHS.CATALOG,
      children: [
        {
          title: t('extendedFooter.article'),
          url: 'https://seasiacenter.com/th/news-events/blog/',
        },
        {
          title: t('extendedFooter.package'),
          url: 'https://www.yournextu.com/package/',
        },
        { title: t('extendedFooter.helpCenter'), url: WEB_PATHS.HELP_CENTER },
        {
          title: t('extendedFooter.contactUs'),
          url: 'https://seasiacenter.com/th/contact/',
        },
        {
          title: t('extendedFooter.seac'),
          url: 'https://seasiacenter.com/th/',
        },
      ],
    },
  ];

  const [footerRedirectButton, setFooterRedirectButton] = useState(
    defaultFooterRedirectButton,
  );
  const year = useMemo(() => new Date().getFullYear(), []);

  useEffect(() => {
    if (catalogMenu?.topics?.length) {
      const newFooter = [
        {
          header: t('extendedFooter.catalog'),
          children: catalogMenu.topics.map((topic) => ({
            title: topic.name,
            url: `/catalog?topicLearning=${topic.id}`,
          })),
        },
        ...defaultFooterRedirectButton,
      ];
      setFooterRedirectButton(newFooter);
    }
  }, [catalogMenu]);
  return (
    <footer className="w-full flex-shrink-0 bg-gray-100 pt-15">
      <div className="relative m-auto mb-8 flex max-w-screen-xl flex-col px-6 text-sm text-grayv2-700 lg:mb-[120px] lg:flex-row lg:px-8">
        <div className="max-w-[290px]">
          <Image
            width={104}
            height={46}
            src={DEFAULT_LOGO}
            alt="YourNextU Logo"
          />
          <div className="mt-8 space-y-4">
            <div>{t('extendedFooter.contact.phone')}</div>
            <div>{t('extendedFooter.contact.email')}</div>
            <div>
              {`${t('extendedFooter.address.line1')} ${t(
                'extendedFooter.address.line2',
              )}`}
              <br />
              {`${t('extendedFooter.address.line3')}`}
            </div>
          </div>
        </div>
        <div className="mt-8 flex flex-1 flex-col justify-between space-y-8 text-xs lg:mx-16 lg:mt-0 lg:flex-row lg:space-x-4 lg:space-y-0 lg:text-sm">
          {footerRedirectButton.map((rb, i) => (
            <div key={`column-${i}`} className="max-w-[240px] flex-1 space-y-4">
              {rb.url ? (
                <Link href={rb.url} passHref>
                  <a className="font-semibold">{rb.header}</a>
                </Link>
              ) : (
                <div className="font-semibold">{rb.header}</div>
              )}
              {rb.children.map((child, j) => (
                <div key={`child-${i}-${j}`}>
                  <Link href={child.url}>
                    <a>{child.title}</a>
                  </Link>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="absolute right-6 top-0 lg:relative">
          <LanguageDropdown />
        </div>
      </div>
      <div className="m-auto max-w-screen-xl">
        <div className="flex flex-col justify-between px-6 py-6 lg:flex-row lg:items-center lg:px-8">
          <div className="order-2 flex flex-1 flex-col space-y-[10px] text-xs text-grayv2-700 lg:flex-row lg:space-y-0 lg:text-sm">
            <span>{t('extendedFooter.copyright')}</span>
            <div className="flex">
              <div className="mx-4 hidden h-[17px] border-r border-grayv2-700 lg:block" />
              <Link href={CORPORATE_WEB_PATHS.PRIVACY_POLICY}>
                <a>{t('footer.privacyPolicy')}</a>
              </Link>
              <div className="mx-4 h-[17px] border-r border-grayv2-700" />
              <Link href={CORPORATE_WEB_PATHS.TERMS_OF_USE}>
                <a>{t('footer.termsAndConditions')}</a>
              </Link>
            </div>
          </div>
          <div className="order-1 mb-4 flex space-x-4 text-grayv2-700 lg:order-3 lg:mb-0">
            {footerSocialMediaIconsConfigs.map((config, i) => (
              <SocialMediaButton
                key={config.url + i}
                Icon={config.icon}
                url={config.url}
              />
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
